import React from "react";
import Marquee from "react-fast-marquee";

/**
 * Classic CityRail orange dot-matrix LED PID.
 * Amber on black, VT323 pixelated typeface, scrolling announcement.
 */
export default function ClassicLEDDisplay({
  destination,
  departureTime,
  platform,
  trainType,
  stops,
  announcement,
  bookingRequired,
  showFareDuration,
  fare,
  duration,
}) {
  const hasAnnouncement = announcement && announcement.trim().length > 0;
  const stopsLine = stops && stops.length > 0 ? `STOPS AT: ${stops.join("  •  ")}` : "";
  const bookingLine = bookingRequired ? "★ BOOKING REQUIRED · RESERVED SEATING ★" : "";
  const fareBits = [];
  if (showFareDuration && duration?.trim()) fareBits.push(`DURATION: ${duration.trim().toUpperCase()}`);
  if (showFareDuration && fare?.trim()) fareBits.push(`FARE: ${fare.trim().toUpperCase()}`);
  const fareLine = fareBits.join("  ·  ");
  const marqueeText = [bookingLine, fareLine, announcement, stopsLine].filter(Boolean).join("        ✦        ");

  return (
    <div
      data-testid="classic-led-display"
      className="relative w-full h-full bg-black overflow-hidden select-none font-vt323"
    >
      {/* Subtle scanlines + CRT vignette */}
      <div className="absolute inset-0 led-scanlines" />
      <div className="absolute inset-0 crt-vignette" />

      {/* Border frame */}
      <div className="absolute inset-0 border-[6px] border-[#1a1a1a]" />

      <div className="relative z-10 w-full h-full flex flex-col p-[clamp(8px,2vw,28px)]">
        {/* Header line: train type + platform */}
        <div className="flex items-baseline justify-between led-glow uppercase">
          <span className="text-[clamp(13px,3.2vw,40px)] tracking-widest truncate pr-2" data-testid="led-train-type">
            {trainType || "NEXT TRAIN"}
          </span>
          <span className="text-[clamp(13px,3.2vw,40px)] tracking-widest shrink-0" data-testid="led-platform-label">
            PLAT {platform || "–"}
          </span>
        </div>

        {/* Destination line - the hero */}
        <div className="flex-1 flex items-center">
          <div
            className="led-glow uppercase leading-none w-full text-center truncate"
            style={{ fontSize: "clamp(32px, 11vw, 180px)" }}
            data-testid="led-destination"
          >
            {destination || "ENTER DESTINATION"}
          </div>
        </div>

        {/* Departure time strip */}
        <div className="flex items-baseline justify-between led-glow uppercase pb-2">
          <span className="text-[clamp(14px,3.6vw,46px)] tracking-widest">
            DEP <span className="blink-soft">·</span>
          </span>
          <span className="text-[clamp(18px,4.6vw,60px)] tracking-widest" data-testid="led-departure">
            {departureTime || "--:--"}
          </span>
        </div>

        {/* Scrolling announcement / stops */}
        <div
          className="border-t-[3px] border-[#3a2700] pt-2"
          data-testid="led-marquee-container"
        >
          {marqueeText ? (
            <Marquee gradient={false} speed={55} pauseOnHover className="led-glow uppercase">
              <span className="text-[clamp(12px,2.6vw,36px)] tracking-widest pr-24">
                {marqueeText}
              </span>
            </Marquee>
          ) : (
            <div className="led-glow uppercase text-[clamp(12px,2.6vw,36px)] tracking-widest opacity-80">
              ※ AWAITING ANNOUNCEMENT ※
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
